import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, ShieldCheck, User, Eye, Users, Wrench } from "@phosphor-icons/react";

const ROLE_META = {
  admin: { label: "Administrador", color: "#FACC15", Icon: ShieldCheck, desc: "Acceso total al sistema" },
  operator: { label: "Operador", color: "#0055FF", Icon: Wrench, desc: "Puede crear y editar citas" },
  viewer: { label: "Solo lectura", color: "#9CA3AF", Icon: Eye, desc: "Solo puede consultar" },
  user: { label: "Operador (legacy)", color: "#0055FF", Icon: Wrench, desc: "Rol antiguo, equivalente a Operador" },
};

export default function Approvals() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [pendingRoles, setPendingRoles] = useState({}); // user_id -> chosen role

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", { params: tab === "all" ? {} : { status: tab } });
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const approve = async (u) => {
    const role = pendingRoles[u.id] || "operator";
    try {
      await api.post(`/users/${u.id}/approve`, null, { params: { role } });
      toast.success(`${u.username} aprobado como ${ROLE_META[role].label}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (u) => {
    if (!window.confirm(`¿Rechazar al usuario ${u.username}?`)) return;
    try {
      await api.post(`/users/${u.id}/reject`);
      toast.success("Usuario rechazado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeRole = async (u, role) => {
    if (!window.confirm(`Cambiar rol de ${u.username} a ${ROLE_META[role].label}?`)) return;
    try {
      await api.post(`/users/${u.id}/role`, null, { params: { role } });
      toast.success("Rol actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
          Administración · Usuarios
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Aprobación de Usuarios</h1>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {["admin", "operator", "viewer"].map((k) => {
          const m = ROLE_META[k];
          const Icon = m.Icon;
          return (
            <div
              key={k}
              className="flex items-start gap-3 p-4 rounded-sm border"
              style={{ borderColor: `${m.color}40`, backgroundColor: `${m.color}0d` }}
            >
              <Icon size={20} weight="duotone" style={{ color: m.color }} />
              <div>
                <div className="font-display text-sm" style={{ color: m.color }}>
                  {m.label}
                </div>
                <div className="text-xs text-white/60">{m.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-1 mb-4 border-b border-white/10">
        {[
          { k: "pending", label: "Pendientes" },
          { k: "approved", label: "Aprobados" },
          { k: "rejected", label: "Rechazados" },
          { k: "all", label: "Todos" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${
              tab === t.k
                ? "border-[#0055FF] text-white"
                : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-[#111111] border border-white/10 rounded-sm">
        {loading ? (
          <div className="p-8 text-white/50 text-sm">Cargando…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center">
            <div className="border border-dashed border-white/10 rounded-sm p-8 max-w-md mx-auto">
              <Users size={36} className="text-white/20 mx-auto mb-3" weight="duotone" />
              <div className="text-white/70">Sin usuarios en esta categoría</div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono-data uppercase tracking-widest text-white/40">
                <tr className="border-b border-white/10">
                  <th className="text-left px-5 py-3">Usuario</th>
                  <th className="text-left px-5 py-3">Nombre</th>
                  <th className="text-left px-5 py-3">Rol</th>
                  <th className="text-left px-5 py-3">Estado</th>
                  <th className="text-right px-5 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const roleMeta = ROLE_META[u.role] || ROLE_META.viewer;
                  const RIcon = roleMeta.Icon;
                  return (
                    <tr
                      key={u.id}
                      data-testid="user-row"
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-5 py-3 font-mono-data">{u.username}</td>
                      <td className="px-5 py-3 text-white/80">{u.full_name || "—"}</td>
                      <td className="px-5 py-3">
                        {u.status === "approved" ? (
                          <select
                            data-testid="user-role-select"
                            value={u.role}
                            onChange={(e) => changeRole(u, e.target.value)}
                            className="bg-transparent border border-white/10 rounded-sm px-2 py-1 text-xs focus:border-[#0055FF] outline-none"
                            style={{ color: roleMeta.color }}
                          >
                            <option value="admin">Administrador</option>
                            <option value="operator">Operador</option>
                            <option value="viewer">Solo lectura</option>
                            {u.role === "user" && <option value="user">Legacy</option>}
                          </select>
                        ) : (
                          <span className="inline-flex items-center gap-1" style={{ color: roleMeta.color }}>
                            <RIcon size={12} weight="fill" /> {roleMeta.label}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <StatusChip s={u.status} />
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          {u.status !== "approved" && (
                            <>
                              <select
                                data-testid="pending-role-select"
                                value={pendingRoles[u.id] || "operator"}
                                onChange={(e) =>
                                  setPendingRoles((p) => ({ ...p, [u.id]: e.target.value }))
                                }
                                className="px-2 py-1.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white outline-none focus:border-[#0055FF]"
                              >
                                <option value="operator">Operador</option>
                                <option value="viewer">Solo lectura</option>
                                <option value="admin">Administrador</option>
                              </select>
                              <button
                                data-testid="approve-btn"
                                onClick={() => approve(u)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-sm bg-[#10B981]/15 border border-[#10B981]/40 text-[#10B981] text-xs font-semibold hover:bg-[#10B981]/25 transition-colors"
                              >
                                <CheckCircle size={12} weight="fill" /> Aprobar
                              </button>
                            </>
                          )}
                          {u.status !== "rejected" && u.status !== "approved" && (
                            <button
                              data-testid="reject-btn"
                              onClick={() => reject(u)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-sm bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] text-xs font-semibold hover:bg-[#FF3B30]/25 transition-colors"
                            >
                              <XCircle size={12} weight="fill" /> Rechazar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusChip({ s }) {
  const map = {
    pending: { c: "#FACC15", t: "Pendiente" },
    approved: { c: "#10B981", t: "Aprobado" },
    rejected: { c: "#FF3B30", t: "Rechazado" },
  };
  const cfg = map[s] || { c: "#9CA3AF", t: s };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm font-mono-data text-[10px] uppercase tracking-widest border"
      style={{ color: cfg.c, borderColor: `${cfg.c}55`, backgroundColor: `${cfg.c}15` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg.c }} />
      {cfg.t}
    </span>
  );
}
